import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Gauge, Percent, Power } from "lucide-react";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { StatCard } from "../../../components/AthenaPricingEngine/stat-card";
import { FilterBar } from "../../../components/AthenaPricingEngine/filter-bar";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import { Skeleton } from "../../../ui/skeleton";
import { GroupedBarChart } from "../../../components/AthenaPricingEngine/charts/grouped-bar-chart";
import { TrendLineChart } from "../../../components/AthenaPricingEngine/charts/trend-line-chart";
import {
  RISK_GRADES,
  useStateOfPlaySummary,
  type ActualVsTargetPoint,
  type StateOfPlaySummary,
} from "../lib/api/state-of-play";
import { rbpEnabledMap, useTargetPolicies } from "../lib/api/manage-targets";
import { useIndustries, useOffices } from "../lib/api/reference";

/** Volume-weighted average yield per grade, for the grouped bar chart. */
function byGrade(points: ActualVsTargetPoint[]) {
  return RISK_GRADES.map((grade) => {
    const rows = points.filter((point) => point.grade === grade);
    const volume = rows.reduce((sum, row) => sum + row.volume, 0) || 1;
    const weighted = (key: "actualYield" | "targetYield") =>
      Number((rows.reduce((sum, row) => sum + row[key] * row.volume, 0) / volume).toFixed(2));
    return {
      grade,
      actualYield: weighted("actualYield"),
      targetYield: weighted("targetYield"),
    };
  });
}

/** Blended RAV/Required Yield/Actual Yield by month, aligned to the same
 * month labels the yield series uses. */
function ravTrend(data: StateOfPlaySummary) {
  return data.yieldTrend.map((row, i) => ({
    month: row.month,
    blendedRav: Number((data.blendedRav.sparkline[i]?.value ?? data.blendedRav.current).toFixed(2)),
    requiredYield: row.requiredYield,
    actualYield: row.actualYield,
  }));
}

function delta(current: number, previous: number) {
  const diff = current - previous;
  return {
    value: `${diff >= 0 ? "+" : ""}${diff.toFixed(1)}% vs previous period`,
    direction: (diff > 0 ? "up" : diff < 0 ? "down" : "flat") as "up" | "down" | "flat",
  };
}

/** Relative change from the previous period, in %. A loss rate going up is the bad direction. */
function relativeChange(current: number | null, previous: number | null) {
  if (current == null || previous == null || previous === 0) return undefined;
  const change = ((current - previous) / previous) * 100;
  return {
    value: `${change >= 0 ? "+" : ""}${change.toFixed(1)}% vs previous period`,
    direction: (change > 0 ? "up" : change < 0 ? "down" : "flat") as "up" | "down" | "flat",
    goodDirection: "down" as const,
  };
}

const ALL_OFFICES_LABEL = "All Offices";
const ALL_INDUSTRIES_LABEL = "All Industry";

export default function StateOfPlay() {
  const officesQuery = useOffices();
  const offices = officesQuery.data ?? [];
  const [officeName, setOfficeName] = useState<string>(ALL_OFFICES_LABEL);
  const office =
    officeName === ALL_OFFICES_LABEL
      ? undefined
      : offices.find((o) => o.officeName === officeName)?.officeNumber;
  const [borrowerIndustry, setBorrowerIndustry] = useState<string>(ALL_INDUSTRIES_LABEL);

  const industriesQuery = useIndustries(office);
  const industries = industriesQuery.data ?? [];

  const navigate = useNavigate();
  const policiesQuery = useTargetPolicies();
  const rbpMap = policiesQuery.data ? rbpEnabledMap(policiesQuery.data) : null;
  const activelyPriced = rbpMap
    ? offices.filter((o) => rbpMap[o.officeName] !== false).length
    : null;

  const { data, isPending, isError, error } = useStateOfPlaySummary({
    office,
    borrowerIndustry,
  });

  return (
    <div>
      <PageHeader title="State Of Play" subtitle="Is pricing healthy, today" />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Pricing health could not be read.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending || !data ? (
        <LoadingState />
      ) : (
        <div className="mt-8 space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              link={{ to: "/risk-pricing-engine/segments" }}
              label="Blended Net RAV"
              value={`${data.blendedRav.current.toFixed(1)}%`}
              icon={Gauge}
              sparkline={data.blendedRav.sparkline}
              trend={{
                ...delta(data.blendedRav.current, data.blendedRav.previousPeriod),
                goodDirection: "up",
              }}
              hint="Risk-adjusted value across everything written this period."
            />
            <StatCard
              link={{ to: "/risk-pricing-engine/exceptions" }}
              label="Segments Below Floor"
              value={String(data.segmentsBelowFloor.count)}
              icon={AlertTriangle}
              sparkColor="var(--decline)"
              sparkline={data.segmentsBelowFloor.sparkline}
              hint="Each one needs an exception decision or a target reset."
            />
            <StatCard
              link={{ to: "/risk-pricing-engine/annualized-loss" }}
              label="Annualized Loss"
              value={
                data.annualizedLoss.current == null ? "—" : `${data.annualizedLoss.current.toFixed(2)}%`
              }
              icon={Percent}
              sparkline={data.annualizedLoss.sparkline}
              trend={relativeChange(data.annualizedLoss.current, data.annualizedLoss.previousPeriod)}
              hint="Annualized Loss of matured book"
            />
            <StatCard
              link={{ to: "/risk-pricing-engine/manage-targets" }}
              label="Offices Actively Priced"
              value={
                activelyPriced == null ? "—" : `${activelyPriced} of ${offices.length}`
              }
              icon={Power}
              hint="Offices with risk-based pricing switched on. The rest are priced by hand."
            />
          </div>

          <Panel
            title="Actual Vs Required By Grade"
            description="Volume-weighted required yield against what was realised, by risk grade."
          >
            <FilterBar
              className="mb-5"
              filters={[
                {
                  id: "office",
                  label: "Office Name",
                  value: officeName,
                  options: [ALL_OFFICES_LABEL, ...offices.map((o) => o.officeName)],
                  onChange: setOfficeName,
                },
                {
                  id: "industry",
                  label: "Borrower Industry",
                  value: borrowerIndustry,
                  options: [ALL_INDUSTRIES_LABEL, ...industries],
                  onChange: setBorrowerIndustry,
                },
              ]}
            />
            <GroupedBarChart
              data={byGrade(data.actualVsTarget)}
              categoryKey="grade"
              // One colour per SERIES NAME, applied product-wide so a reader carries
              // the meaning from chart to chart:
              //   Required / target -> Athena yellow
              //   Actual            -> Navitas blue
              //   Blended RAV       -> Athena green
              // These used to borrow --grade-b1 and --grade-a, the colours of risk
              // grades B1 and A, so a series read as a grade.
              series={[
                { dataKey: "targetYield", name: "Required Yield", color: "var(--athena-yellow)" },
                { dataKey: "actualYield", name: "Actual Yield", color: "var(--navitas-blue)" },
              ]}
              onBarClick={(row) =>
                navigate(`/risk-pricing-engine/segments?grade=${encodeURIComponent(row.grade)}`)
              }
            />
            <p className="mt-3 text-xs text-muted-foreground">
              Select a grade&apos;s bar to open Segments filtered to that grade.
            </p>
          </Panel>

          <Panel
            title="Blended Net RAV, Required Yield & Actual Yield By Month"
            description="Risk-adjusted value against required and actual yield across the book."
          >
            <TrendLineChart
              data={ravTrend(data)}
              // Same three-way rule as the bar chart above.
              series={[
                { dataKey: "blendedRav", name: "Blended Net RAV", color: "var(--athena-green)" },
                { dataKey: "requiredYield", name: "Required Yield", color: "var(--athena-yellow)" },
                { dataKey: "actualYield", name: "Actual Yield", color: "var(--navitas-blue)" },
              ]}
            />
          </Panel>
        </div>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="mt-8 space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="border border-border bg-card p-5">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="mt-5 h-8 w-24" />
            <Skeleton className="mt-4 h-3 w-40" />
          </div>
        ))}
      </div>
      <div className="border border-border bg-card p-5">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="mt-5 h-9 w-52" />
        <Skeleton className="mt-5 h-[420px] w-full" />
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="border border-border bg-card p-5">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="mt-5 h-[300px] w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
